import React, { useRef, useState } from 'react'
import EmailVerificationEnvelopeIllustration from './EmailVerificationEnvelopeIllustration'
import EmailVerificationButtonLabel from './EmailVerificationButtonLabel'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const INVALID_EMAIL_ERROR = 'Please enter a valid email address.'

// Shared screen for both the "I use a different email now" (Flow A) and
// "add backup email" (Flow B) steps: collects an address. Re-auth (password,
// or Google/Apple) is handled separately by ReauthenticationModal when the
// API responds with a 403 reauth_required.
const EmailVerificationEmailPasswordForm = (props) => {
  const {
    title, description, emailLabel, submitLabel, currentEmail, backupEmail, showIllustration,
    duplicateEmailError, currentBackupEmail, sameAsCurrentBackupError,
    submitLoading, errorMessage, onSubmit, onBack, backLabel,
    onSkip, skipLabel, onNeverAskAgain,
  } = props
  const [email, setEmail] = useState('')
  const [validationError, setValidationError] = useState('')
  // checked live against the address as typed (not on submit), so the
  // heads-up about retiring the backup shows before they press the button
  const matchesBackupEmail = !!backupEmail &&
    email.trim().toLowerCase() === backupEmail.toLowerCase()
  // also checked live, so the duplicate errors show as soon as they're typed
  const matchesCurrentEmail = !!currentEmail &&
    email.trim().toLowerCase() === currentEmail.toLowerCase()
  // only passed when replacing an existing backup
  const matchesCurrentBackupEmail = !!currentBackupEmail &&
    email.trim().toLowerCase() === currentBackupEmail.toLowerCase()
  // errors about the address as typed: they clear as soon as it's fixed, so
  // submit stays unavailable while one is up (request errors from the server
  // don't block it, since retrying the same address can succeed)
  const addressError = validationError ||
    (matchesCurrentEmail && duplicateEmailError) ||
    (matchesCurrentBackupEmail && sameAsCurrentBackupError)
  const shownError = addressError || errorMessage
  const submitDisabled = submitLoading || !!addressError

  // set on mousedown, which fires before the input's blur — see handleEmailBlur
  const pressingButtonRef = useRef(false)

  function handleEmailChange(e) {
    const { value } = e.target
    setEmail(value)
    // the invalid-address error stays up until it's actually fixed (or
    // cleared), rather than vanishing on the first keystroke
    const trimmed = value.trim()
    if (!trimmed || EMAIL_REGEX.test(trimmed)) setValidationError('')
  }

  // the format check runs on leaving the field rather than live, since an
  // address is "invalid" right up until it's nearly typed out
  function handleEmailBlur() {
    // skipped when leaving via one of the form's buttons: the error appearing
    // would shift them down mid-click so the click could miss — submit runs
    // the same check itself, and the others leave the form anyway
    if (pressingButtonRef.current) {
      pressingButtonRef.current = false
      return
    }
    const trimmed = email.trim()
    if (trimmed && !EMAIL_REGEX.test(trimmed)) setValidationError(INVALID_EMAIL_ERROR)
  }

  function handleMouseDown(e) {
    pressingButtonRef.current = !!e.target.closest('button')
  }

  function handleSubmit(e) {
    e.preventDefault()
    const trimmedEmail = email.trim()
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setValidationError(INVALID_EMAIL_ERROR)
      return
    }
    // already showing live (see shownError)
    if (matchesCurrentEmail || matchesCurrentBackupEmail) return
    setValidationError('')
    onSubmit({ email: trimmedEmail })
  }

  return (
    <div className='emailVerificationFlow-form' onMouseDown={handleMouseDown}>
      {onBack && (
        <button type='button' className='emailVerificationFlow-backBtn' onClick={onBack}>
          {backLabel || '← Back'}
        </button>
      )}
      {showIllustration && <EmailVerificationEnvelopeIllustration />}
      <div className='emailVerificationFlow-title'>{title}</div>
      {description && (
        <div className='emailVerificationFlow-description'>{description}</div>
      )}
      <form className='emailVerificationFlow-form-main' onSubmit={handleSubmit}>
        <label className='emailVerificationFlow-form-label' htmlFor='emailVerificationFlow-emailInput'>
          {emailLabel || 'Email address'}
        </label>
        <input
          id='emailVerificationFlow-emailInput'
          className='emailVerificationFlow-form-input'
          type='email'
          autoComplete='email'
          value={email}
          onChange={handleEmailChange}
          onBlur={handleEmailBlur}
          autoFocus
        />
        {shownError && (
          <div className='emailVerificationFlow-error'>{shownError}</div>
        )}
        {matchesBackupEmail && !validationError && (
          <div className='emailVerificationFlow-notice'>
            This is your backup email address. By continuing, you&apos;ll no longer have a backup email address and may want to consider adding a new one.
          </div>
        )}
        <button
          type='submit'
          className={`btn btn--blue btn--large${submitDisabled ? ' btn--disabled' : ''}`}
          disabled={submitDisabled}
        >
          <EmailVerificationButtonLabel label={submitLabel || 'Send me a code'} loading={submitLoading} />
        </button>
      </form>
      {onSkip && (
        <div className='emailVerificationFlow-form-links'>
          {onNeverAskAgain && (
            <button type='button' className='emailVerificationFlow-linkBtn' onClick={onNeverAskAgain}>
              Don&apos;t ask me again
            </button>
          )}
          <button
            type='button'
            className='emailVerificationFlow-linkBtn'
            onClick={onSkip}
          >
            {skipLabel || 'Not now'}
          </button>
        </div>
      )}
    </div>
  )
}

EmailVerificationEmailPasswordForm.defaultProps = {
  showIllustration: true,
  duplicateEmailError: 'Please enter a different email address.',
}

export default EmailVerificationEmailPasswordForm
